import ELayersView from 'enums/ELayersView';
import React, { useCallback, useEffect, useState } from 'react';
import { FragmentLayerDTO } from 'business/models/Models';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { cloneDeep } from 'lodash';
import {
  selectExperimentLayers,
  selectExp,
} from 'redux/selectors/ExperimentStateSelector';
import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import { InputWithUnit } from 'components/input';
import LayersError from 'components/customComponents/Layers/LayersError';

interface EditFragmentProps extends WithTabBackButtonStateProps {
  selectedFragmentLayer: FragmentLayerDTO;
  setContentView: (e) => void;
  containerMaxDropVolume: number;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  shouldReturnToLayersExplorer?: boolean;
  onApplyComplete?: () => void;
}

export const EditFragment = (props: EditFragmentProps) => {
  const {
    selectedFragmentLayer,
    setContentView,
    containerMaxDropVolume,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    shouldReturnToLayersExplorer = false,
    onApplyComplete,
  } = props;
  const dispatch = useRMDispatch();
  const exp = useRMSelector(selectExp) as any;
  const [error, setError] = useState<boolean>(false);
  const [volume, setVolume] = useState<string>(() => {
    if (
      selectedFragmentLayer?.volume == null &&
      exp?.containerDefaultFragmentVolume != null
    ) {
      return exp.containerDefaultFragmentVolume.toString();
    }
    return selectedFragmentLayer?.volume ? selectedFragmentLayer?.volume.toString() : '0';
  });

  const callPreserveUndoableState = usePreserveExperiment();
  const _layers = useRMSelector(selectExperimentLayers);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  const applyScreen = () => {
    callPreserveUndoableState();
    const name = selectedFragmentLayer?.fragmentScreen?.treeNode?.name;
    // RFR: we need to write a reducer function for this update to avoid cloneDeep
    const tempLayers = cloneDeep(_layers);
    const index = tempLayers.findIndex((l) => l.id == selectedFragmentLayer.layerId);
    const layersWithoutCurrent = _layers.filter(
      (l) => l.id !== selectedFragmentLayer.layerId,
    );
    tempLayers[index].name = getUniqueLayerName(layersWithoutCurrent, name);
    tempLayers[index].dirty = true;
    tempLayers[index].layerDetail = selectedFragmentLayer;
    tempLayers[index].layerDetail.volume = parseFloat(volume);
    _setLayers(tempLayers);
    setContentView(ELayersView.LAYERDESIGNVIEW);
    handleBackToPreviousScreen(null);
    setHandleOnTabBackPress(null);

    if (shouldReturnToLayersExplorer && onApplyComplete) {
      onApplyComplete();
    }
  };

  function handleVolumeChange(e) {
    if (isNaN(e.target.value)) {
      return;
    }
    setVolume(e.target.value);
    if (Number(e.target.value) > containerMaxDropVolume || e.target.value === '') {
      setError(true);
    } else {
      setError(false);
    }
  }

  useEffect(() => {
    setHandleOnTabBackPress(() => () => {
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW)();
    });
  }, [setHandleOnTabBackPress]);

  return (
    <div
      id="commandpanel-fragmentlayer-editfragment-wrapper"
      className="flex flex-column flex-x-center gap-2"
      style={{ padding: 24 }}
    >
      <div
        id="commandpanel-fragmentlayer-editfragment-fragmentname-wrapper"
        className="flex flex-col w-full text-sm gap-[0.4rem] text-black"
      >
        <label
          id="commandpanel-fragmentlayer-editfragment-fragmentname-label"
          className="block"
        >
          Fragment Name
        </label>
        <div
          id="abc"
          className="hover:cursor-pointer block w-full border border-black px-3 py-[0.45rem] text-[#0356fc] font-medium"
          onClick={() => setContentView(ELayersView.SELECTFRAGMENTSCREEN)}
        >
          {selectedFragmentLayer?.fragmentScreen?.treeNode?.name}
        </div>
      </div>
      <div
        id="commandpanel-fragmentlayer-editfragment-conditions-wrapper"
        className="flex flex-col w-full text-sm gap-[0.4rem] text-black"
      >
        <label
          id="commandpanel-fragmentlayer-editfragment-conditions-label"
          className="block"
        >
          Conditions
        </label>
        <div
          id="abc"
          className="hover:cursor-pointer block w-full border border-black px-3 py-[0.45rem] bg-gray-200 font-medium"
        >
          {`${selectedFragmentLayer?.fragmentScreen?.fragmentFragmentScreen?.length} conditions`}
        </div>
      </div>
      <InputWithUnit
        id="volume-input"
        unit="µL"
        labelText="Volume"
        inputValue={volume}
        onChange={handleVolumeChange}
        error={error}
      />
      {error && (
        <LayersError
          id="volume-error"
          errorText="Entered volume is invalid."
          style={{ marginTop: '1rem' }}
        />
      )}
      <div
        id="commandpanel-fragmentlayer-editfragment-actions"
        className="flex justify-end mt-2"
      >
        <RMCustomButton
          id="apply-button"
          title="Apply"
          contentContainerClass="apply-button"
          titleClass="apply-button-title"
          onClick={applyScreen}
          isDisabled={error}
        />
      </div>
    </div>
  );
};

export default withTabBackButton(EditFragment);
